/**
 * Word lists for the hyphen and dash checker. Written for this tool from the conventions
 * the Chicago Manual of Style (18th ed.) and the APA Publication Manual (7th ed.) both
 * follow; every compound below is hyphenated before a noun and open after one.
 */

const SPACE = /\s+/;

function list(words: string): string[] {
  return words.trim().split(SPACE);
}

/** [open form, hyphenated form]. The open form is lowercase, words separated by "_". */
const COMPOUND_SOURCE = `
  well_known high_quality low_quality long_term short_term low_income high_income
  middle_class full_time part_time real_world peer_reviewed cross_sectional
  self_reported double_blind single_blind open_ended close_ended semi_structured
  large_scale small_scale wide_ranging far_reaching time_consuming labor_intensive
  hands_on face_to_face one_on_one day_to_day step_by_step in_depth up_to_date
  state_of_the_art so_called evidence_based data_driven case_control long_standing
  decision_making problem_solving high_level low_level high_risk low_risk high_profile
  first_year second_year third_year fourth_year first_time gender_based
  community_based school_based population_based web_based computer_based
  research_based theory_driven theory_based student_centered learner_centered
  patient_centered user_friendly cost_effective cost_benefit time_limited
  goal_directed work_related age_related health_related
`;

export interface Compound {
  /** "well known" */
  open: string;
  /** "well-known" */
  hyphenated: string;
}

export const COMPOUNDS: readonly Compound[] = list(COMPOUND_SOURCE).map(
  (entry) => ({
    open: entry.split("_").join(" "),
    hyphenated: entry.split("_").join("-"),
  })
);

/** -ly adverbs that often get a stray hyphen: "highly-educated". */
export const LY_ADVERBS: readonly string[] = list(`
  highly newly fully widely poorly recently previously closely strongly heavily
  largely mostly nearly partially partly randomly significantly statistically
  socially culturally economically politically historically clinically medically
  genetically internationally nationally locally globally rapidly slowly carefully
  clearly directly freely deeply mildly severely moderately relatively particularly
  uniquely primarily finely tightly loosely brightly densely sparsely thinly
  thickly richly badly barely scarcely truly purely simply totally completely
  entirely roughly publicly privately professionally officially formally
  informally legally technically theoretically empirically experimentally
  manually automatically digitally physically mentally emotionally
  psychologically biologically chemically naturally artificially environmentally
  ecologically geographically demographically systematically consistently
  frequently commonly typically normally usually generally originally finally
  initially eventually ultimately immediately continuously increasingly steadily
  gradually sharply slightly substantially considerably dramatically markedly
  vastly hugely
`);

/** Statistics and science terms named after two people: Chicago 18 sets them with an en dash. */
export const NAME_PAIRS: readonly string[] = list(`
  Kaplan-Meier Mann-Whitney Kruskal-Wallis Shapiro-Wilk Kolmogorov-Smirnov
  Durbin-Watson Hardy-Weinberg Michaelis-Menten Bland-Altman Epstein-Barr
  Lineweaver-Burk Benjamini-Hochberg Box-Cox Yerkes-Dodson Hodgkin-Huxley
  Rescorla-Wagner Lotka-Volterra Black-Scholes Gram-Schmidt Cauchy-Schwarz
  Navier-Stokes
`);

export const WEEKDAYS: readonly string[] = list(
  "Monday Tuesday Wednesday Thursday Friday Saturday Sunday"
);

export const MONTHS: readonly string[] = list(`
  January February March April May June July August September October November
  December
`);

/** Words after which a compound is not modifying a noun, so no hyphen is needed. */
export const NOT_A_NOUN: ReadonlySet<string> = new Set(
  list(`
    and or but nor so yet for to in on at of by with from as than that which who
    whom whose when where while whereas if is are was were be been being am has have
    had do does did will would can could may might must shall should not no too
    enough again also only even still just very more most less rather quite because
    since until although though after before about over under through during into
    onto between among against within without per via then there here it its they
    them their he she we you i
  `)
);

/** Units that make "a 5 year old child" a compound modifier. */
export const UNIT_WORDS: readonly string[] = list(`
  second minute hour day week month year decade century page word point step item
  level mile inch foot yard meter metre kilometer kilometre
`);

export const NUMBER_WORDS: readonly string[] = list(`
  one two three four five six seven eight nine ten eleven twelve fifteen twenty
  thirty forty fifty
`);
